import React, { useState } from 'react';
import { Send, ArrowLeft, FileQuestion } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { complaintsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';

const GrievanceRedressal = () => {
  const { member } = useAuth();
  const currentMemberId = member?.member_id;
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    subject: 'Grievance / Query - One Globe Alliance',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentMemberId) {
      toast.error('Please sign in to submit a grievance');
      return;
    }
    const message = (form.message || '').trim();
    if (!message) {
      toast.error('Please enter your grievance or query details');
      return;
    }
    try {
      setSubmitting(true);
      await complaintsApi.submitGrievance(
        { subject: form.subject.trim() || 'Grievance / Query - One Globe Alliance', message },
        currentMemberId
      );
      toast.success('Your grievance/query has been sent. We will respond within 2–3 business days.');
      setForm({ subject: 'Grievance / Query - One Globe Alliance', message: '' });
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to send grievance'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-start">
        <Link to="/complaints">
          <Button
            variant="outline"
            size="default"
            className="border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
          >
            <ArrowLeft size={18} className="mr-2" />
            Back to Complaints
          </Button>
        </Link>
      </div>

      <Card className="border-blue-200 bg-gradient-to-br from-slate-50 to-blue-50 max-w-2xl mx-auto">
        <CardHeader>
          <div className="flex items-center gap-2">
            <FileQuestion size={28} className="text-blue-600" />
            <CardTitle className="text-2xl">Grievance Redressal</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-gray-700">
            Not all issues are against member companies. For general grievances, membership queries, or to contact
            One Globe Alliance (OGA) directly, use the form below. Our team will respond within 2–3 business days.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4 pt-2">
            <div>
              <Label htmlFor="grievance-subject">Subject</Label>
              <Input
                id="grievance-subject"
                className="mt-1"
                value={form.subject}
                onChange={(e) => setForm((prev) => ({ ...prev, subject: e.target.value }))}
                placeholder="e.g. Grievance / Query - One Globe Alliance"
                required
              />
            </div>
            <div>
              <Label htmlFor="grievance-message">Message</Label>
              <Textarea
                id="grievance-message"
                className="mt-1 min-h-[160px]"
                value={form.message}
                onChange={(e) => setForm((prev) => ({ ...prev, message: e.target.value }))}
                placeholder="Please describe your grievance or query in detail, including any relevant dates, references, and your contact details."
                required
              />
            </div>
            <Button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={submitting}
              data-testid="grievance-submit-btn"
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Sending...
                </span>
              ) : (
                <>
                  <Send size={18} className="mr-2" />
                  Send to OGA
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default GrievanceRedressal;
